/**
 * Theme utility functions for PageRenderer components
 */

export const getThemeAwareStyles = (darkMode, theme, customSettings = {}) => {
  return {
    // Background colors
    backgroundColor:
      customSettings.backgroundColor ||
      (darkMode ? theme.palette.grey[800] : theme.palette.background.paper),

    cardBackground:
      customSettings.cardBackground ||
      (darkMode ? theme.palette.grey[700] : theme.palette.background.paper),

    // Text colors
    primaryText:
      customSettings.primaryText ||
      (darkMode ? theme.palette.common.white : theme.palette.text.primary),

    secondaryText:
      customSettings.secondaryText ||
      (darkMode ? theme.palette.grey[300] : theme.palette.text.secondary),

    // Button colors
    primaryButton:
      customSettings.primaryButton ||
      (darkMode ? theme.palette.primary.light : theme.palette.primary.main),

    secondaryButton:
      customSettings.secondaryButton ||
      (darkMode ? theme.palette.secondary.light : theme.palette.secondary.main),

    // Border colors
    borderColor:
      customSettings.borderColor ||
      (darkMode ? theme.palette.grey[600] : theme.palette.grey[300]),

    // Shadow
    boxShadow:
      customSettings.boxShadow ||
      (darkMode ? "0 4px 8px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.1)"),

    // Hover states
    hoverBackground:
      customSettings.hoverBackground ||
      (darkMode ? theme.palette.grey[600] : theme.palette.grey[100]),
  };
};

export const getButtonStyles = (
  darkMode,
  theme,
  buttonType = "primary",
  size = "medium"
) => {
  const baseStyles = {
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 600,
    transition: "all 0.3s ease",
  };

  const sizeStyles = {
    small: { padding: "8px 16px", fontSize: "0.875rem" },
    medium: { padding: "12px 24px", fontSize: "1rem" },
    large: { padding: "16px 32px", fontSize: "1.125rem" },
  };

  const typeStyles = {
    primary: {
      backgroundColor: darkMode
        ? theme.palette.primary.light
        : theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover": {
        backgroundColor: darkMode
          ? theme.palette.primary.main
          : theme.palette.primary.dark,
        transform: "translateY(-2px)",
        boxShadow: darkMode
          ? "0 6px 12px rgba(0,0,0,0.4)"
          : "0 4px 12px rgba(0,0,0,0.2)",
      },
    },
    secondary: {
      backgroundColor: "transparent",
      color: darkMode ? theme.palette.common.white : theme.palette.text.primary,
      border: `2px solid ${
        darkMode ? theme.palette.common.white : theme.palette.primary.main
      }`,
      "&:hover": {
        backgroundColor: darkMode
          ? theme.palette.primary.light
          : theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        transform: "translateY(-2px)",
      },
    },
  };

  return {
    ...baseStyles,
    ...sizeStyles[size],
    ...typeStyles[buttonType],
  };
};

export const getCardStyles = (darkMode, theme, elevated = false) => {
  return {
    backgroundColor: darkMode
      ? theme.palette.grey[800]
      : theme.palette.background.paper,
    color: darkMode ? theme.palette.common.white : theme.palette.text.primary,
    borderRadius: "12px",
    boxShadow: elevated
      ? darkMode
        ? "0 8px 24px rgba(0,0,0,0.4)"
        : "0 4px 20px rgba(0,0,0,0.1)"
      : darkMode
      ? "0 4px 12px rgba(0,0,0,0.3)"
      : "0 2px 8px rgba(0,0,0,0.1)",
    border: darkMode ? `1px solid ${theme.palette.grey[700]}` : "none",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: darkMode
        ? "0 12px 32px rgba(0,0,0,0.5)"
        : "0 8px 32px rgba(0,0,0,0.15)",
    },
  };
};
